import Compressor from 'compressorjs';

const DEFAULT_TARGET_COMPRESSED_SIZE = 150 * 1024;

export type CompressImageOptions = {
    /** Aim for file size at or below this (bytes). Default ~150KB. */
    targetBytes?: number;
    maxWidth?: number;
    maxHeight?: number;
};

/** Compress images before upload. Falls back to original on error. */
export function compressImageForUpload(file: File, options?: CompressImageOptions): Promise<File> {
    if (!file.type.startsWith('image/')) {
        return Promise.resolve(file);
    }

    const targetBytes = options?.targetBytes ?? DEFAULT_TARGET_COMPRESSED_SIZE;
    const maxWidth = options?.maxWidth ?? 1600;
    const maxHeight = options?.maxHeight ?? 1600;

    return new Promise<File>((resolve, reject) => {
        let quality = 0.7;
        if (file.size > 5 * 1024 * 1024) {
            quality = 0.5;
        } else if (file.size > 2 * 1024 * 1024) {
            quality = 0.6;
        }

        new Compressor(file, {
            quality,
            maxWidth,
            maxHeight,
            convertSize: targetBytes,
            success: compressedFile => {
                const compressedImageFile = new File([compressedFile], file.name, { type: compressedFile.type });

                if (compressedImageFile.size > targetBytes && quality > 0.3) {
                    quality = Math.max(0.3, quality - 0.2);
                    new Compressor(file, {
                        quality,
                        maxWidth: Math.min(1200, maxWidth),
                        maxHeight: Math.min(1200, maxHeight),
                        convertSize: targetBytes,
                        success: moreCompressedFile => {
                            resolve(
                                new File([moreCompressedFile], file.name, {
                                    type: moreCompressedFile.type,
                                }),
                            );
                        },
                        error: err => reject(err),
                    });
                } else {
                    resolve(compressedImageFile);
                }
            },
            error: err => reject(err),
        });
    }).catch((): File => file);
}
